<script setup lang="ts">
/**
 * A mailbox's contacts (#1114), the Contacts tab of the mailbox panel: everyone it wrote to, saved as each send left,
 * and everyone added by hand, most recently written to first. They can be searched, added (an address and a name),
 * renamed and removed. A name set here is kept over any a later send carries. A CSV or vCard file imports into the
 * book, merged by address, and the book downloads as either (#1120). Everything goes through `contacts`, so the same
 * tab keeps a person's own book and, for an owner, a member's.
 */
import { Download, Pencil, Trash2, Upload, UserPlus } from '@lucide/vue'
import { formatChatFileSize, MAIL_CONTACTS_IMPORT_LIMITS, mailContactsImportLimitsSentence } from '@zerohuman/shared'
import { Button, Input, Separator, ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import type { MailContact, MailContactsImportResult } from '@/api'
import ListSearchField from '@/components/ListSearchField.vue'
import type { MailboxContacts } from '@/components/MailboxPanel.vue'
import { uploadsOf } from '@/lib/mailAttachments'

const props = withDefaults(defineProps<{ contacts: MailboxContacts; own?: boolean }>(), { own: false })

/** How long typing in the search pauses before the list is read again. */
const PAUSE_MS = 250

const rows = ref<MailContact[]>([])
const search = ref('')
const loading = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)
const busy = ref<string | null>(null)
const adding = ref(false)
const newAddress = ref('')
const newName = ref('')
const editingId = ref<string | null>(null)
const editName = ref('')
const fileInput = ref<HTMLInputElement | null>(null)
let timer: ReturnType<typeof setTimeout> | undefined
/** Which read the list shows, so a read that returns after a newer one is dropped. */
let asked = 0

/** `lead`, and the server's sentence after it when the refusal carried one: a bare code adds nothing. */
function failureText(caught: unknown, lead: string): string {
    const said = caught instanceof Error ? caught.message.trim() : ''
    return /\s/.test(said) ? `${lead} ${said}` : lead
}

/** When the mailbox last wrote to a contact, as a person reads it. */
function lastWritten(at: string | null): string {
    if (!at) return 'Not written to yet'
    const day = new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    return `Last written ${day}`
}

async function load(): Promise<void> {
    const mine = ++asked
    loading.value = true
    error.value = null
    try {
        const found = await props.contacts.list({ search: search.value, limit: 200 })
        if (mine === asked) rows.value = found
    } catch {
        if (mine === asked) {
            rows.value = []
            error.value = 'Could not load the contacts.'
        }
    } finally {
        if (mine === asked) loading.value = false
    }
}

function onSearch(value: string | number): void {
    search.value = String(value)
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => void load(), PAUSE_MS)
}

async function command(name: string, run: () => Promise<void>): Promise<void> {
    if (busy.value) return
    busy.value = name
    error.value = null
    try {
        await run()
    } finally {
        busy.value = null
    }
}

async function add(): Promise<void> {
    const address = newAddress.value.trim()
    if (address === '') return
    const name = newName.value.trim()
    await command('add', async () => {
        try {
            await props.contacts.save(name ? { address, name } : { address })
        } catch (caught) {
            error.value = failureText(caught, 'Could not add this contact.')
            return
        }
        notice.value = `Added ${name || address}.`
        newAddress.value = ''
        newName.value = ''
        adding.value = false
        await load()
    })
}

function startRename(contact: MailContact): void {
    editingId.value = contact.id
    editName.value = contact.name
}

async function rename(contact: MailContact): Promise<void> {
    if (!contact.id) return
    const id = contact.id
    const name = editName.value.trim()
    await command(`rename:${id}`, async () => {
        try {
            await props.contacts.rename(id, name)
        } catch (caught) {
            error.value = failureText(caught, 'Could not rename this contact.')
            return
        }
        notice.value = name ? `Renamed to ${name}.` : 'Name cleared.'
        editingId.value = null
        await load()
    })
}

async function remove(contact: MailContact): Promise<void> {
    if (!contact.id) return
    const id = contact.id
    await command(`remove:${id}`, async () => {
        try {
            await props.contacts.remove(id)
        } catch (caught) {
            error.value = failureText(caught, 'Could not remove this contact.')
            return
        }
        notice.value = `Removed ${contact.name || contact.address}.`
        await load()
    })
}

/** What an import did, as one sentence a person reads. */
function importSummary(result: MailContactsImportResult): string {
    const count = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`
    const changed = [
        result.added > 0 ? `added ${count(result.added, 'contact')}` : '',
        result.updated > 0 ? `named ${count(result.updated, 'contact')} that had no name` : '',
    ].filter(Boolean)
    const parts = [changed.length > 0 ? `Imported: ${changed.join(', ')}.` : 'Nothing new to import.']
    if (result.unchanged > 0)
        parts.push(`${count(result.unchanged, 'address', 'addresses')} already here, left as they were.`)
    if (result.skipped > 0) {
        const shown =
            result.invalid.length > 0
                ? ` (${result.invalid.join(', ')}${result.skipped > result.invalid.length ? ', …' : ''})`
                : ''
        parts.push(`${count(result.skipped, 'entry', 'entries')} skipped for having no valid address${shown}.`)
    }
    return parts.join(' ')
}

/** Imports the file picked (#1120): refused here when it is over the limit, before anything is read. */
async function importPicked(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    // The same file may be picked again.
    target.value = ''
    if (!file) return
    if (file.size > MAIL_CONTACTS_IMPORT_LIMITS.maxBytes) {
        notice.value = null
        error.value = `${file.name} is ${formatChatFileSize(file.size)}; a contacts file holds ${mailContactsImportLimitsSentence()}.`
        return
    }
    await command('import', async () => {
        try {
            const [upload] = await uploadsOf([file])
            if (!upload) return
            notice.value = importSummary(await props.contacts.import(upload))
        } catch (caught) {
            notice.value = null
            error.value = failureText(caught, `Could not import ${file.name}.`)
            return
        }
        await load()
    })
}

onMounted(() => void load())
onBeforeUnmount(() => {
    if (timer) clearTimeout(timer)
})
</script>

<template>
	<div class="space-y-3" data-testid="mail-contacts">
		<div class="flex flex-wrap items-end gap-2">
			<label class="grid min-w-48 flex-1 gap-1 text-sm">
				Search contacts
				<ListSearchField :model-value="search" type="search" placeholder="Name or address" data-testid="mail-contacts-search" @update:model-value="onSearch" />
			</label>
			<Button type="button" size="sm" variant="outline" data-testid="mail-contacts-add" :disabled="busy !== null" @click="adding = !adding">
				<UserPlus class="size-3.5" aria-hidden="true" />
				Add contact
			</Button>
			<Button
				type="button"
				size="sm"
				variant="outline"
				data-testid="mail-contacts-import"
				:disabled="busy !== null"
				:title="`A CSV or vCard file, ${mailContactsImportLimitsSentence()}`"
				@click="fileInput?.click()"
			>
				<Upload class="size-3.5" aria-hidden="true" />
				{{ busy === 'import' ? 'Importing…' : 'Import' }}
			</Button>
			<input
				ref="fileInput"
				type="file"
				accept=".csv,.vcf,.vcard,text/csv,text/vcard,text/x-vcard"
				class="sr-only"
				tabindex="-1"
				aria-label="Import contacts from a CSV or vCard file"
				data-testid="mail-contacts-import-input"
				@change="importPicked"
			/>
			<div class="flex items-center gap-1" role="group" aria-label="Export contacts">
				<Button as-child size="sm" variant="outline">
					<a :href="contacts.exportUrl('csv')" download="contacts.csv" data-testid="mail-contacts-export-csv">
						<Download class="size-3.5" aria-hidden="true" />
						Export CSV
					</a>
				</Button>
				<Button as-child size="sm" variant="outline">
					<a :href="contacts.exportUrl('vcard')" download="contacts.vcf" data-testid="mail-contacts-export-vcard">
						<Download class="size-3.5" aria-hidden="true" />
						Export vCard
					</a>
				</Button>
			</div>
		</div>
		<form v-if="adding" class="flex flex-wrap items-end gap-2" data-testid="mail-contacts-add-form" @submit.prevent="add">
			<label class="grid min-w-48 flex-1 gap-1 text-sm">
				Address
				<Input v-model="newAddress" type="email" required data-testid="mail-contacts-new-address" />
			</label>
			<label class="grid min-w-40 flex-1 gap-1 text-sm">
				Name
				<Input v-model="newName" data-testid="mail-contacts-new-name" />
			</label>
			<Button type="submit" size="sm" :disabled="busy !== null || newAddress.trim() === ''">Save</Button>
			<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="adding = false">Cancel</Button>
		</form>
		<p v-if="notice" class="text-sm" data-testid="mail-contacts-notice" aria-live="polite">{{ notice }}</p>
		<p v-if="error" class="text-destructive text-sm" data-testid="mail-contacts-error">{{ error }}</p>
		<p v-else-if="loading && !rows.length" class="text-muted-foreground text-sm">Loading contacts…</p>
		<p v-else-if="!rows.length" class="text-muted-foreground text-sm" data-testid="mail-contacts-empty">
			<template v-if="search.trim()">No contact matches {{ search.trim() }}.</template>
			<template v-else>
				No contacts yet. {{ own ? 'Everyone you write to is saved here,' : 'Everyone this mailbox writes to is saved here,' }} and you can add one yourself or import a CSV or vCard file.
			</template>
		</p>
		<ZhPanel v-else as="div" flush>
			<ul>
				<li v-for="(contact, index) in rows" :key="contact.address" :data-contact-id="contact.id ?? undefined">
					<Separator v-if="index > 0" />
					<form
						v-if="editingId !== null && editingId === contact.id"
						class="flex flex-wrap items-end gap-2 px-3 py-2"
						data-testid="mail-contacts-rename-form"
						@submit.prevent="rename(contact)"
					>
						<label class="grid min-w-48 flex-1 gap-1 text-sm">
							Name for {{ contact.address }}
							<Input v-model="editName" data-testid="mail-contacts-rename-name" />
						</label>
						<Button type="submit" size="sm" :disabled="busy !== null">Save</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="editingId = null">Cancel</Button>
					</form>
					<div v-else class="flex items-center gap-2 px-3 py-2 text-sm">
						<div class="flex min-w-0 flex-1 flex-col gap-0.5">
							<span class="font-medium" data-contact-name>{{ contact.name || contact.address }}</span>
							<span class="text-muted-foreground truncate text-xs">
								<template v-if="contact.name">{{ contact.address }} · </template>
								{{ lastWritten(contact.lastMailedAt) }}<template v-if="contact.mailCount > 0"> · {{ contact.mailCount }} sent</template>
							</span>
						</div>
						<ZhSystemLabel v-if="contact.source === 'manual'" as="span" tone="muted">Added by hand</ZhSystemLabel>
						<Button
							type="button"
							size="sm"
							variant="ghost"
							data-contact-rename
							:aria-label="`Rename ${contact.name || contact.address}`"
							:disabled="busy !== null"
							@click="startRename(contact)"
						>
							<Pencil class="size-4" aria-hidden="true" />
						</Button>
						<Button
							type="button"
							size="sm"
							variant="ghost"
							data-contact-remove
							:aria-label="`Remove ${contact.name || contact.address}`"
							:disabled="busy !== null"
							@click="remove(contact)"
						>
							<Trash2 class="size-4" aria-hidden="true" />
						</Button>
					</div>
				</li>
			</ul>
		</ZhPanel>
	</div>
</template>
